import { simpleAxios } from "@/utils/http"
import Dexie from "dexie"
import type { AxiosResponse } from "axios"
import { markRaw, toRaw } from "vue"
import { useRoute } from "vue-router"
import { useIpadHuntStore } from "@/stores/apps/ipad_hunt"
import { useContentStore } from "@/stores/apps/content"
import { useEventsStore } from "@/stores/apps/events"
import { sound_alert } from "@/utils/notifications"
import { compressPlayerMediaForUpload } from "@/utils/playerMediaUpload"

/** Reject missing / placeholder team ids that would omit team_id in JSON or send "undefined" in FormData. */
const isValidTeamId = (teamId: unknown): boolean => {
	if (teamId == null || teamId === "") return false
	const asString = String(teamId)
	return asString !== "undefined" && asString !== "null" && asString !== "NaN"
}

/** Real Blob/File only — persisted Pinia state turns File into `{}`, which breaks multipart. */
const isValidUploadBlob = (file: unknown): file is Blob => {
	const raw = toRaw(file)
	return typeof Blob !== "undefined" && raw instanceof Blob && raw.size > 0
}

/** Fresh File copy so Safari/IndexedDB blobs serialize correctly into FormData. */
const toUploadFile = async (file: Blob, fileName: string): Promise<File> => {
	const raw = toRaw(file) as Blob
	const buffer = await raw.arrayBuffer()
	return new File([buffer], fileName || "upload.bin", {
		type: raw.type || "application/octet-stream",
		lastModified: Date.now(),
	})
}

const buildSubmitFormData = async (
	teamId: unknown,
	challenge: unknown,
	file: Blob,
	fileName: string
): Promise<FormData> => {
	const uploadFile = await toUploadFile(file, fileName)
	const data = new FormData()
	data.append("team_id", String(teamId))
	data.append("challenge", String(challenge))
	data.append("file", uploadFile, uploadFile.name)
	return data
}

/** Avoid concurrent updateData runs double-posting the same queue rows. */
let updateDataInFlight: Promise<void> | null = null

/** Prefer current_team; if empty, try setTeam from the player route (same idea as PlayerIpadHunt). */
const resolveTeamId = (): number | string | null => {
	const eventStore = useEventsStore()
	const currentId = (eventStore.current_team as { id?: number | string } | null | undefined)?.id
	if (isValidTeamId(currentId)) {
		return currentId as number | string
	}
	try {
		const route = useRoute()
		const teamApiKey = route.params?.team_api_key
		if (teamApiKey == null || teamApiKey === "") {
			return null
		}
		const team = eventStore.setTeam(String(teamApiKey)) as { id?: number | string } | undefined
		const resolved = team?.id
		return isValidTeamId(resolved) ? (resolved as number | string) : null
	} catch {
		return null
	}
}

const initDB = () => {
	const db = new Dexie("Content")
	db.version(1).stores({
		challenge:
			"id, data, file1, file2, file3,match_a,match_b,match_c,match_d,match_e,match_f,match_1,match_2,match_3,match_4,match_5,match_6",
		answers: " challenge, team_id, answer",
		file_upload: "challenge, team_id, file, name",
		challenge_done: " challenge",
		challenge_started: "id, done, challenge, start, force, start_time"
	})
	return db
}

const match_images_key = ["match_a","match_b","match_c","match_d","match_e","match_f","match_1","match_2","match_3","match_4","match_5","match_6"]

const files = ["file_1_es","file_2_es","file_3_es","file_1_en","file_2_en","file_3_en"]

const IPAD_GATEWAY_BLOB_KEYS = [...files, ...match_images_key] as const

/**
 * One-time geolocation check used before opening Team WS in iPad Hunt player bootstrap.
 * QR mode skips this pre-check.
 */
const ensureIpadHuntPreTeamWsGeolocation = async (): Promise<void> => {
	const ipadStore = useIpadHuntStore()
	if ((ipadStore.ipadData as { type?: string }).type === "QR") {
		return
	}
	if (!navigator.geolocation) {
		ipadStore.modalErrorLocation = true
		return
	}
	await new Promise<void>((resolve) => {
		navigator.geolocation.getCurrentPosition(
			() => {
				ipadStore.modalErrorLocation = false
				resolve()
			},
			(error: GeolocationPositionError) => {
				if (error.code === 1) {
					ipadStore.modalErrorLocation = true
				}
				resolve()
			},
			{
				enableHighAccuracy: true,
				timeout: 15000,
				maximumAge: 10000
			}
		)
	})
}


const notifyChallengeProgress = (challengeId: number, flags: { start?: boolean; pending?: boolean }, api_key?: string) => {
	if (challengeId == -1 || !api_key) {
		return
	}
	const teamId = resolveTeamId()
	if (!isValidTeamId(teamId)) {
		return
	}
	// Fire-and-forget: must not block the player while ranking updates for the monitor
	simpleAxios("post", "IpadHunt/challenge_progress/" + api_key, {
		team_id: teamId,
		challenge: challengeId,
		start: !!flags.start,
		pending: !!flags.pending,
	}).catch(() => {})
}

// create a function to store data and remove once is upload
// also to create a function to save

const sendAnswers = async (data, challengeId, api_key?: string) => {
	if( challengeId == -1)
	{
		// IF id == -1 is that is in preview mode
		return;
	}
	const teamId = resolveTeamId()
	if (!isValidTeamId(teamId)) {
		console.warn("[iPadHunt] sendAnswers skipped: missing team_id")
		return
	}
	const ipadStore = useIpadHuntStore();

	const db = initDB()
	// eventStore.current_ipad_team.challenges.find(e => e.id == challengeId).done = true

	try{
		await db.answers.add({
			challenge: challengeId,
			answer: data,
			team_id: teamId,
		})
	}
	catch
	{
		ipadStore.sendAnswers.push({
			challenge: challengeId,
			answer: data,
			team_id: teamId,
		})
	}

	await db.challenge_done.add({
		challenge: challengeId
	})
	
	await db.table("challenge_started").where('challenge').equals(challengeId).modify(item => {
		item.done = true
	})
	ipadStore.updateStoreChallengeToStartDb();
	sound_alert("finish_challange");
	notifyChallengeProgress(challengeId, { pending: true, start: true }, api_key)
	
	// If QR mode and api_key provided, immediately send to server
	if (api_key && ipadStore.ipadData?.type === 'QR') {
		await updateData(api_key)
	}
}

const sendFile = async (data, challengeId, name, api_key?: string) => {
	if( challengeId == -1)
	{
		// IF id == -1 is that is in preview mode
		return;
	}
	const teamId = resolveTeamId()
	if (!isValidTeamId(teamId)) {
		console.warn("[iPadHunt] sendFile skipped: missing team_id")
		return
	}
	const db = initDB()
	const ipadStore = useIpadHuntStore();

	const file =
		data instanceof Blob
			? await compressPlayerMediaForUpload(data, name || "upload.bin")
			: data

	if (!isValidUploadBlob(file)) {
		console.warn("[iPadHunt] sendFile skipped: invalid file blob")
		return
	}

	const fileName = file instanceof File ? file.name : name
	// markRaw: Pinia must not Proxy the Blob (breaks FormData multipart).
	const storedFile = markRaw(toRaw(file))

	try {
		await db.file_upload.put({
			challenge: challengeId,
			file: storedFile,
			team_id: teamId,
			name: fileName,
		})
	} catch (error) {
		console.warn("[iPadHunt] file_upload IndexedDB put failed, using memory queue", error)
		ipadStore.sendFiles.push({
			challenge: challengeId,
			file: storedFile,
			team_id: teamId,
			name: fileName,
		})
	}

	await db.challenge_done.add({
		challenge: challengeId
	})
	await db.table("challenge_started").where('challenge').equals(challengeId).modify(item => {
		item.done = true
	})
	ipadStore.updateStoreChallengeToStartDb();
	sound_alert("finish_challange");
	notifyChallengeProgress(challengeId, { pending: true, start: true }, api_key)
	
	// If QR mode and api_key provided, immediately send to server
	if (api_key && ipadStore.ipadData?.type === 'QR') {
		await updateData(api_key)
	}
}

const getChallengeDone = async () => {
	const db = initDB()
	const data = await db.challenge_done.toArray()

	return data.map(obj => obj.challenge)
}


/** SubmitAnswer returns HTTP 200 even when scoring is "incorrect"; only "missing file" should keep the local retry queue. */
const shouldClearSubmitQueue = (response: AxiosResponse) => {
	return response?.data?.data !== "missing file"
}

/** Drop poisoned queue rows (no team_id / no real file) so the retry loop cannot flood the API. */
const dropInvalidQueuedSubmit = async (
	kind: "answers" | "file_upload" | "sendAnswers" | "sendFiles",
	challenge: unknown,
	ipadStore: ReturnType<typeof useIpadHuntStore>,
	db: Dexie,
	index?: number
) => {
	console.warn("[iPadHunt] dropping invalid queued submit", { kind, challenge })
	if (kind === "answers") {
		await db.answers.delete(challenge as number)
		return
	}
	if (kind === "file_upload") {
		await db.file_upload.delete(challenge as number)
		return
	}
	if (kind === "sendAnswers" && index != null) {
		ipadStore.sendAnswers.splice(index, 1)
		return
	}
	if (kind === "sendFiles" && index != null) {
		ipadStore.sendFiles.splice(index, 1)
	}
}

/** Only drop on 400 when local payload is actually bad — empty-body 400s must retry. */
const shouldDropOnClientError = (error: any, localValid: boolean) => {
	if (error?.response?.status !== 400) return false
	if (localValid) return false
	return true
}

const updateData = async (api_key_event: string) => {
	if (updateDataInFlight) {
		return updateDataInFlight
	}

	updateDataInFlight = (async () => {
		const db = initDB()
		const answers = await db.answers.toArray()
		const ipadStore = useIpadHuntStore()

		for (let index = 0; index < answers.length; index++) {
			const e = answers[index]
			const localValid = isValidTeamId(e?.team_id) && e?.challenge != null
			if (!localValid) {
				await dropInvalidQueuedSubmit("answers", e?.challenge, ipadStore, db)
				continue
			}
			await simpleAxios("post", "IpadHunt/submit_answer/" + api_key_event, {
				challenge: e.challenge,
				answer: e.answer,
				team_id: e.team_id,
			})
				.then(async (response: AxiosResponse) => {
					if (!shouldClearSubmitQueue(response)) return
					db.transaction("rw", db.answers, async function () {
						db.answers.delete(e.challenge)
					})
				})
				.catch(async (error: any) => {
					if (shouldDropOnClientError(error, localValid)) {
						await dropInvalidQueuedSubmit("answers", e.challenge, ipadStore, db)
					}
				})
		}

		const file_upload = await db.file_upload.toArray()
		for (let index = 0; index < file_upload.length; index++) {
			const e = file_upload[index]
			const localValid =
				isValidTeamId(e?.team_id) && e?.challenge != null && isValidUploadBlob(e?.file)
			if (!localValid) {
				await dropInvalidQueuedSubmit("file_upload", e?.challenge, ipadStore, db)
				continue
			}
			const data = await buildSubmitFormData(e.team_id, e.challenge, e.file, e.name || "upload.bin")
			await simpleAxios("post", "IpadHunt/submit_answer/" + api_key_event, data)
				.then(async (response: AxiosResponse) => {
					if (!shouldClearSubmitQueue(response)) return
					db.transaction("rw", db.file_upload, async function () {
						db.file_upload.delete(e.challenge)
					})
				})
				.catch(async (error: any) => {
					if (shouldDropOnClientError(error, localValid)) {
						await dropInvalidQueuedSubmit("file_upload", e.challenge, ipadStore, db)
					}
				})
		}

		for (let index = ipadStore.sendAnswers.length - 1; index >= 0; index--) {
			const e = ipadStore.sendAnswers[index]
			const localValid = isValidTeamId(e?.team_id) && e?.challenge != null
			if (!localValid) {
				await dropInvalidQueuedSubmit("sendAnswers", e?.challenge, ipadStore, db, index)
				continue
			}
			await simpleAxios("post", "IpadHunt/submit_answer/" + api_key_event, {
				challenge: e.challenge,
				answer: e.answer,
				team_id: e.team_id,
			})
				.then(async (response: AxiosResponse) => {
					if (!shouldClearSubmitQueue(response)) return
					ipadStore.sendAnswers.splice(index, 1)
				})
				.catch(async (error: any) => {
					if (shouldDropOnClientError(error, localValid)) {
						await dropInvalidQueuedSubmit("sendAnswers", e.challenge, ipadStore, db, index)
					}
				})
		}

		for (let index = ipadStore.sendFiles.length - 1; index >= 0; index--) {
			const e = ipadStore.sendFiles[index]
			const localValid =
				isValidTeamId(e?.team_id) && e?.challenge != null && isValidUploadBlob(e?.file)
			if (!localValid) {
				await dropInvalidQueuedSubmit("sendFiles", e?.challenge, ipadStore, db, index)
				continue
			}
			const data = await buildSubmitFormData(e.team_id, e.challenge, e.file, e.name || "upload.bin")
			await simpleAxios("post", "IpadHunt/submit_answer/" + api_key_event, data)
				.then(async (response: AxiosResponse) => {
					if (!shouldClearSubmitQueue(response)) return
					ipadStore.sendFiles.splice(index, 1)
				})
				.catch(async (error: any) => {
					if (shouldDropOnClientError(error, localValid)) {
						await dropInvalidQueuedSubmit("sendFiles", e.challenge, ipadStore, db, index)
					}
				})
		}
	})().finally(() => {
		updateDataInFlight = null
	})

	return updateDataInFlight
}

function degreesToRadians(degrees) {
	return (degrees * Math.PI) / 180
}

function haversineDistance(lat1, lon1, lat2, lon2) {
	// Convert decimal degrees to radians
	lat1 = degreesToRadians(lat1)
	lon1 = degreesToRadians(lon1)
	lat2 = degreesToRadians(lat2)
	lon2 = degreesToRadians(lon2)

	// Haversine formula
	const dlon = lon2 - lon1
	const dlat = lat2 - lat1
	const a = Math.pow(Math.sin(dlat / 2), 2) + Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(dlon / 2), 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
	const radiusOfEarth = 6371 // Radius of the Earth in kilometers
	const distance = radiusOfEarth * c * 1000 // Convert to meters
	return distance
}

function isPointInsideCircle(centerLat, centerLon, pointLat, pointLon, radius) {
	// Calculate the distance between the center point and the given point
	const distance = haversineDistance(centerLat, centerLon, pointLat, pointLon)

	// Check if the distance is less than or equal to the radius
	return distance <= radius
}


const deleteAllcontent = async () => {
	const dbs = await window.indexedDB.databases()
	dbs.forEach(db => {
		window.indexedDB.deleteDatabase(db.name)
	})
	location.reload()
}

/**
 * Clears only the player-progress tables (answers, file_upload, challenge_done,
 * challenge_started) without touching cached media or reloading the page.
 * Call this when demo mode is stopped so the next demo cycle starts clean.
 */
const clearDemoProgressDb = async () => {
	try {
		const db = initDB()
		await db.open()
		await Promise.all([
			db.table("answers").clear(),
			db.table("file_upload").clear(),
			db.table("challenge_done").clear(),
			db.table("challenge_started").clear(),
		])
	} catch (e) {
		console.warn("[iPadHunt] clearDemoProgressDb error:", e)
	}
}

const activeChallenge = async (data, api_key?: string) => {
	// Ensure we have at most 1 row per challenge in challenge_started.
	const db = initDB()
	const challengeId = data?.id
	// content id used to query `challenge` table
	// In some flows `data.challenge` can be undefined if we changed data shape.
	const contentId =
		data?.challenge ??
		data?.contentId ??
		challengeId

	// If already completed locally, don't re-create a started challenge.
	if (challengeId === undefined || challengeId === null) {
		console.warn("[iPadHunt] activeChallenge missing id", data)
		return
	}
	if (contentId === undefined || contentId === null) {
		console.warn("[iPadHunt] activeChallenge missing contentId", data)
		return
	}

	const doneRow = await db.challenge_done.get(challengeId)
	if (doneRow) {
		const ipadStore = useIpadHuntStore()
		ipadStore.updateStoreChallengeToStartDb()
		return
	}

	const existing = await db.challenge_started.get(challengeId)
	if (existing && existing.done === true) {
		const ipadStore = useIpadHuntStore()
		ipadStore.updateStoreChallengeToStartDb()
		return
	}

	const content = await db.challenge.where("id").equals(contentId).first()
	const currentDate = new Date()
	const seconds = content?.data?.time ?? 60
	const futureDate = new Date(currentDate.getTime() + seconds * 1000)

	// Use deterministic primary key id = challengeId, and put (upsert) to avoid duplicates.
	await db.challenge_started.put({
		id: challengeId,
		done: false,
		challenge: challengeId,
		start: true,
		force: false,
		start_time: futureDate,
	})

	const ipadStore = useIpadHuntStore()
	ipadStore.updateStoreChallengeToStartDb()
	notifyChallengeProgress(challengeId, { start: true }, api_key)
}

const getActiveChallenge = async()=>{
	// create new challenge data.
	const db = initDB()
	return await db.challenge_started.toArray()
} 

const dumpIndexedDbContent = async () => {
	const db = initDB()
	const tableNames = ["challenge", "answers", "file_upload", "challenge_done", "challenge_started"]

	const toLogSafe = (value: any) => {
		if (value instanceof Blob) {
			return { __type: "Blob", type: value.type, size: value.size }
		}
		return value
	}

	const sanitize = (row: any) => {
		if (row == null || typeof row !== "object") return row
		if (Array.isArray(row)) return row.map(sanitize)

		const out: Record<string, any> = {}
		for (const [k, v] of Object.entries(row)) out[k] = sanitize(toLogSafe(v))
		return out
	}

	console.groupCollapsed("[iPadHunt][IndexedDB] Dexie DB: Content")
	for (const name of tableNames) {
		try {
			const rows = await db.table(name).toArray()
			console.groupCollapsed(`[iPadHunt][IndexedDB] table: ${name} (${rows.length})`)
			console.log(rows.map(sanitize))
			console.groupEnd()
		} catch (e) {
			console.warn(`[iPadHunt][IndexedDB] failed reading table: ${name}`, e)
		}
	}
	console.groupEnd()
}



export {
	ensureIpadHuntPreTeamWsGeolocation,
	sendAnswers,
	sendFile,
	updateData,
	getChallengeDone,
	isPointInsideCircle,
	deleteAllcontent, 
	activeChallenge, 
	getActiveChallenge,
	dumpIndexedDbContent,
	clearDemoProgressDb,
}
